import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

export const initAnimations = () => {
  // Register ScrollTrigger once
  gsap.registerPlugin(ScrollTrigger);

  // Desktop breakpoint (≥1280px)
  const DESKTOP_QUERY = "(min-width: 1024px)";
  const mm = gsap.matchMedia();

  // Common settings
  const settings = { duration: 0.6 };

  // Helper: attach ScrollTrigger to a timeline
  const initScrollTrigger = (tl, el, start = "top bottom-=20%") => {
    ScrollTrigger.create({
      animation: tl,
      trigger: el,
      start: () => start,
    });
  };

  // Helper: default "rise up" animation tuple
  const getDefaultAnim = (from, to) => ([
    { opacity: 0, y: "100px", ...from },
    { opacity: 1, y: 0, duration: settings.duration, stagger: 0.4, ease: "power1.out", ...to },
  ]);

  // Selectors we own (used to safely kill only our triggers)
  const OWN_SELECTORS = [
    "[data-rise-up]",
    "[data-fade-in]",
    "[data-anim-right]",
    "[data-anim-left]",
    "[data-rise-list]",
    "[data-left-list]",
  ].join(", ");

  // Mobile behavior: kill triggers and set elements to final state
  const disableAnimsOnMobile = () => {
    // Kill only our ScrollTriggers
    ScrollTrigger.getAll().forEach(st => {
      if (!st.trigger) return;
      if (st.trigger.matches(OWN_SELECTORS) || st.trigger.closest(OWN_SELECTORS)) st.kill();
    });

    // Set final styles (as if animations already completed)
    document.querySelectorAll(OWN_SELECTORS).forEach(el => {
      if (el.matches('[data-rise-list]')) {
        gsap.set(el.querySelectorAll('[data-rise-item]'), { clearProps: "all", x: 0, y: 0, opacity: 1 });
      } else if (el.matches('[data-left-list]')) {
        gsap.set(el.querySelectorAll('[data-left-item]'), { clearProps: "all", x: 0, y: 0, opacity: 1 });
      } else {
        gsap.set(el, { clearProps: "all", x: 0, y: 0, opacity: 1 });
      }
    });
  };

  // Desktop-only init: all your timelines wrapped in a gsap.context
  mm.add(DESKTOP_QUERY, () => {
    const ctx = gsap.context(() => {
      // === riseUp ===
      const riseUp = document.querySelectorAll("[data-rise-up]");
      riseUp.forEach((el) => {
        const delay = el.getAttribute('data-delay') || 0;
        const start = el.getAttribute('data-start') || 'top bottom-=20%';
        const tl = gsap.fromTo(el, ...getDefaultAnim({}, { delay, duration: settings.duration }));
        if (el.closest('[data-rise-up-group]')) {
          initScrollTrigger(tl, el.closest('[data-rise-up-group]'));
        } else {
          initScrollTrigger(tl, el, start);
        }
      });

      // === fadeIn ===
      const fadeIn = document.querySelectorAll('[data-fade-in]');
      fadeIn.forEach((el) => {
        const delay = el.getAttribute('data-delay') || 0;
        const start = el.getAttribute('data-start') || 'top bottom-=30%';
        const tl = gsap.fromTo(el, { opacity: 0 }, {
          opacity: 1,
          duration: settings.duration,
          ease: "power1.out",
          delay,
        });
        initScrollTrigger(tl, el, start);
      });

      // === animRight ===
      const animRight = document.querySelectorAll('[data-anim-right]');
      animRight.forEach((el) => {
        const delay = el.getAttribute('data-delay') || 0;
        const start = el.getAttribute('data-start') || 'top bottom-=30%';
        const tl = gsap.fromTo(el, { x: '100px', opacity: 0 }, {
          x: 0,
          opacity: 1,
          duration: settings.duration,
          ease: "power1.out",
          delay,
        });
        initScrollTrigger(tl, el, start);
      });

      // === animLeft ===
      const animLeft = document.querySelectorAll('[data-anim-left]');
      animLeft.forEach((el) => {
        const delay = el.getAttribute('data-delay') || 0;
        const start = el.getAttribute('data-start') || 'top bottom-=30%';
        const tl = gsap.fromTo(el, { x: '-100px', opacity: 0 }, {
          x: 0,
          opacity: 1,
          duration: settings.duration,
          ease: "power1.out",
          delay,
        });
        initScrollTrigger(tl, el, start);
      });

      // === listRiseUpAnim ===
      const listRiseUpAnim = document.querySelectorAll('[data-rise-list]');
      listRiseUpAnim.forEach((el) => {
        const items = el.querySelectorAll('[data-rise-item]');
        const start = el.getAttribute('data-start') || 'top bottom-=-20%';
        const delay = el.getAttribute('data-delay') || 0;
        const tl = gsap.fromTo(items, { y: '100', opacity: 0 }, {
          y: 0,
          opacity: 1,
          duration: settings.duration,
          ease: "ease",
          delay,
          stagger: 0.2,
        });
        initScrollTrigger(tl, el, start);
      });

      // === listLeftAnim ===
      const listLeftAnim = document.querySelectorAll('[data-left-list]');
      listLeftAnim.forEach((el) => {
        const items = el.querySelectorAll('[data-left-item]');
        const delay = el.getAttribute('data-delay') || 0;
        const start = el.getAttribute('data-start') || 'top bottom-=-20%';
        const tl = gsap.fromTo(items, { x: '-100px', opacity: 0 }, {
          x: 0,
          opacity: 1,
          duration: settings.duration,
          ease: "ease",
          delay,
          stagger: 0.2,
        });
        initScrollTrigger(tl, el, start);
      });
    });

    // Cleanup when leaving the desktop breakpoint
    return () => ctx.revert();
  });

  // Mobile & tablet (<1024px): ensure animations are disabled and elements are "final"
  mm.add("(max-width: 1023px)", () => {
    disableAnimsOnMobile();
  });
};
